import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { toast } from 'sonner';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';

import { paths } from 'src/routes/paths';

import { API_URL } from 'src/config';

import {
  getTenantIdLS,
  getAccessTokenLS,
  removeTenantIdLS,
  removeAccessTokenLS,
} from './localStorageUtils';

type CustomFetchBaseQueryError = FetchBaseQueryError & {
  originalStatus?: number;
};

const queryHeaders = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers) => {
    const accessToken = getAccessTokenLS();
    const persistRoot = localStorage.getItem('persist:root');

    // If persistRoot is null, clear session and redirect
    if (!persistRoot) {
      removeAccessTokenLS();
      removeTenantIdLS();
      localStorage.removeItem('persist:root');

      toast.error('Session expired. Please log in again.');
      setTimeout(() => {
        window.location.href = paths.auth.signIn;
      }, 1500);

      return headers;
    }

    let userPersistData;
    let loginDtl;

    try {
      userPersistData = JSON.parse(persistRoot);
      loginDtl = JSON.parse(userPersistData.auth)?.loginApiData;
    } catch (err) {
      console.error('Failed to parse login data:', err);
      toast.error('Corrupted session. Please log in again.');

      removeAccessTokenLS();
      removeTenantIdLS();
      localStorage.removeItem('persist:root');

      setTimeout(() => {
        window.location.href = paths.auth.signIn;
      }, 1500);

      return headers;
    }

    const selectedTenantRecord = getTenantIdLS();

    if (accessToken) {
      headers.set('Authorization', `${accessToken}`);
    }

    if (loginDtl?.tenantId) {
      const tenantId = selectedTenantRecord || loginDtl.tenantId;
      headers.set('tenantId', tenantId);
    }

    headers.set('Content-Type', 'application/json');
    return headers;
  },
});

const baseQuery = async (args: string | FetchArgs, api: any, extraOptions: any) => {
  const result = await queryHeaders(args, api, extraOptions);

  const error = result.error as CustomFetchBaseQueryError;

  const errorMessage =
    error && error.data && typeof error.data === 'object' && 'message' in error.data
      ? (error.data as { message?: string }).message
      : undefined;

  if (error?.originalStatus === 403 || error?.status === 403) {
    if (errorMessage === 'Tenant is not ACTIVE' || errorMessage === 'User is not ACTIVE') {
      toast.error('Tenant/User is not active.');
    } else {
      toast.error('Session expired. Please log in again.');
    }

    removeAccessTokenLS();
    removeTenantIdLS();
    localStorage.removeItem('persist:root');

    setTimeout(() => {
      window.location.href = paths.auth.signIn;
    }, 1500);
  }

  return result;
};

export default baseQuery;
